import type {ChangeEvent} from 'react';

export interface VariantOverrideOption {
  idAiPrompt: number;
  alias: string | null;
  isActive: boolean;
  weightPct: number;
}

export interface AgentOverrideRow {
  agentType: string;
  currentAlias: string | null;
  options: VariantOverrideOption[];
}

/** The select value that means "no override" — must stay in sync with the backend's `NO_OVERRIDE` sentinel, which is what tells the server to clear rather than set the override. */
export const NO_OVERRIDE_VALUE = 'auto' as const;

/** Page props carrying each override dropdown's choices, built from the PHP enum by the AI chat controller. */
export const OVERRIDE_OPTIONS_PAGE_PROPS = {
  textRemovalMethod: 'mteForcedTextRemovalMethodOptions',
  textExtractionModel: 'mteForcedTextExtractionModelOptions',
} as const;

export type OverrideOptionsPageProp = (typeof OVERRIDE_OPTIONS_PAGE_PROPS)[keyof typeof OVERRIDE_OPTIONS_PAGE_PROPS];

export type ForcedImageProvider = typeof NO_OVERRIDE_VALUE | 'gemini-3.1-flash-image-preview' | 'openai-gpt-image' | 'openai-gpt-image-sunburst' | 'seedream';

/**
 * A `TextRemovalMethod` value, or {@link NO_OVERRIDE_VALUE}. Not a union of literals: the choices arrive from
 * the server in page props, so the PHP enum stays the only list and adding a backend needs no change here.
 */
export type ForcedTextRemovalMethod = string;

export type MockCredits = typeof NO_OVERRIDE_VALUE | '0' | '1' | '2' | '5' | '20' | '100';

/**
 * A `TextExtractionModel` value, or {@link NO_OVERRIDE_VALUE}. Its cases pair a model WITH a reasoning effort
 * — the two are one choice, because the same model at low and high effort answers differently, costs
 * differently and takes a different amount of time.
 *
 * Not a union of literals: the choices arrive from the server in page props, so the PHP enum stays the only
 * list and adding an arm needs no change here.
 */
export type ForcedTextExtractionModel = string;

export interface OverrideSelectOption<TValue extends string = string> {
  value: TValue;
  label: string;
}

export type ForcedProviderOption = OverrideSelectOption<ForcedImageProvider>;

/** Server-sent, so the value is a plain string rather than a literal union. */
export type ForcedTextRemovalMethodOption = OverrideSelectOption;

export type MockCreditsOption = OverrideSelectOption<MockCredits>;

/** Server-sent, so the value is a plain string rather than a literal union. */
export type ForcedTextExtractionModelOption = OverrideSelectOption;

export interface OverrideSelectHandlerOptions<TValue extends string> {
  isValidValue: (value: string) => value is TValue;
  save: (value: TValue) => Promise<void>;
  onSaved: (value: TValue) => void;
  onError: () => void;
}

export interface OverrideSelectRowProps {
  id: string;
  label: string;
  value: string;
  options: OverrideSelectOption[];
  onChange: (event: ChangeEvent<HTMLSelectElement>) => void;
}
